<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref, watch } from 'vue';
import GamificationAPI from 'dashboard/api/gamification';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'visits'),
    screen: computed(() => 'edit'),
  })
);

const source = computed(() => model.detail?.source || {});
const gamification = ref({
  activities: [],
  loading: false,
  profile: null,
  rules: [],
});
let gamificationRequest = 0;
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const initials = name =>
  String(name || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toUpperCase();
const formatDateTime = value => {
  if (!value) return 'Não informado';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};
const formatDateInput = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value).slice(0, 10);

  return new Intl.DateTimeFormat('pt-BR').format(date);
};
const formatDuration = value => {
  const minutes = Number(value || 0);
  if (!minutes) return 'Não informado';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours}h ${remaining}min` : `${hours} hora${hours > 1 ? 's' : ''}`;
};
const toLocalDateTimeInput = value => {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';

  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const status = computed(() => model.detail?.status || 'Sem status');
const visitCode = computed(() =>
  firstValue(source.value, ['visit_code', 'reference', 'code'], `Visita #${model.detail?.id || '—'}`)
);
const propertyTitle = computed(() =>
  model.detail?.title || firstValue(source.value, ['imovel.titulo', 'imovel.title', 'property.title', 'listing.title'], 'Imóvel não informado')
);
const propertyAddress = computed(() =>
  firstValue(source.value, ['imovel.endereco', 'property.address', 'address.full_address', 'address.endereco'], 'Endereço não informado')
);
const propertyLocation = computed(() =>
  [
    firstValue(source.value, ['imovel.bairro', 'property.neighborhood', 'address.neighborhood']),
    firstValue(source.value, ['imovel.cidade', 'property.city', 'address.city']),
  ]
    .filter(Boolean)
    .join(' • ')
);
const imageUrl = image =>
  typeof image === 'string'
    ? image
    : image?.url || image?.file_url || image?.image_url || image?.cover_url || '';
const propertyImage = computed(() => {
  const directImage = firstValue(source.value, [
    'imovel.cover_url',
    'imovel.image_url',
    'property.cover_url',
    'property.image_url',
    'listing.cover_url',
    'listing.image_url',
    'image_url',
  ]);
  const galleryImage = [
    source.value.imovel?.images?.[0],
    source.value.imovel?.photos?.[0],
    source.value.property?.images?.[0],
    source.value.property?.photos?.[0],
    source.value.imovel?.midias?.[0],
    source.value.imovel?.media?.[0],
    source.value.property?.midias?.[0],
    source.value.property?.media?.[0],
    source.value.listing?.images?.[0],
  ]
    .map(imageUrl)
    .find(Boolean);

  return directImage || galleryImage || '';
});
const propertyFacts = computed(() => {
  const facts = [
    ['i-lucide-bed-double', firstValue(source.value, ['imovel.quartos', 'imovel.bedrooms', 'property.quartos', 'property.bedrooms', 'listing.bedrooms']), 'dorms.'],
    ['i-lucide-bath', firstValue(source.value, ['imovel.banheiros', 'imovel.bathrooms', 'property.banheiros', 'property.bathrooms', 'listing.bathrooms']), 'banheiros'],
    ['i-lucide-car-front', firstValue(source.value, ['imovel.vagas_garagem', 'imovel.parking_spaces', 'property.vagas_garagem', 'property.parking_spaces', 'listing.parking_spaces']), 'vagas'],
    ['i-lucide-ruler', firstValue(source.value, ['imovel.area_util', 'property.area_util', 'property.useful_area', 'listing.area_util']), 'm²'],
  ]
    .filter(([, value]) => value !== '')
    .map(([icon, value, label]) => ({ icon, label: `${value} ${label}` }));

  return facts.length ? facts : model.detailFacts.slice(0, 3).map(item => ({
    icon: 'i-lucide-house',
    label: `${item.label}: ${item.value}`,
  }));
});
const participantFrom = (candidate, role) => {
  if (!candidate) return null;

  const name = firstValue(candidate, [
    'name',
    'full_name',
    'contact.name',
    'user.name',
    'display_name',
  ]);
  if (!name) return null;

  return {
    avatar: firstValue(candidate, ['avatar_url', 'thumbnail', 'contact.thumbnail', 'user.avatar_url']),
    email: firstValue(candidate, ['email', 'contact.email', 'user.email']),
    id: String(candidate.id || candidate.user_id || name),
    initials: initials(name),
    name,
    phone: firstValue(candidate, ['phone_number', 'phone', 'contact.phone_number', 'contact.phone']),
    role,
  };
};
const clients = computed(() =>
  [source.value.contact, source.value.client, source.value.visitor]
    .map(candidate => participantFrom(candidate, 'Cliente'))
    .filter(Boolean)
    .filter((item, index, list) => list.findIndex(candidate => candidate.id === item.id) === index)
    .slice(0, 1)
);
const brokers = computed(() =>
  [source.value.user, source.value.agent, source.value.broker, source.value.assigned_to, source.value.assignedTo, source.value.responsible]
    .map(candidate => participantFrom(candidate, 'Corretor responsável'))
    .filter(Boolean)
    .filter((item, index, list) => list.findIndex(candidate => candidate.id === item.id) === index)
    .slice(0, 1)
);
const collaborators = computed(() => [...clients.value, ...brokers.value]);
const accessInstructions = computed(() =>
  firstValue(source.value, [
    'access_instructions',
    'access_notes',
    'additional_attributes.entry_access.instructions',
    'additional_attributes.entry_access.helper',
    'property.access_instructions',
    'imovel.access_instructions',
  ])
);
const isAccessDetail = value => {
  const normalized = String(value || '').trim().toLocaleLowerCase('pt-BR');

  return normalized && !['-', '—', 'n/a', 'nao informado', 'não informado'].includes(normalized);
};
const accessDetails = computed(() =>
  [
    {
      label: 'Acesso',
      value: firstValue(source.value, ['additional_attributes.entry_access.access_label']),
    },
    {
      label: 'Chave',
      value: firstValue(source.value, ['additional_attributes.entry_access.key_type_label']),
    },
    {
      label: 'Portaria',
      value: firstValue(source.value, ['additional_attributes.entry_access.doorman_label']),
    },
    {
      label: 'Ocupação',
      value: firstValue(source.value, ['additional_attributes.entry_access.house_occupancy_label']),
    },
  ].filter(item => isAccessDetail(item.value))
);
const hasAccessInformation = computed(() =>
  Boolean(accessInstructions.value || accessDetails.value.length)
);
const activityLabel = event => {
  if (event.label || event.title || event.body) {
    return event.label || event.title || event.body;
  }

  return event.status ? `Status da visita: ${event.status}` : 'Atualização da visita';
};
const visitActivities = computed(() => {
  const history = model.history.length
    ? model.history
    : source.value.imovel?.visit_history || [];

  return history.slice(0, 4).map((event, index) => ({
    actor: event.actor || event.actor_name || event.agent_name || event.user?.name || 'Sistema',
    avatar: event.actor?.avatar_url || event.actor_avatar_url || event.user?.avatar_url || '',
    date:
      event.date ||
      event.scheduled_at_label ||
      formatDateTime(event.occurred_at || event.created_at || event.scheduled_at),
    id: event.id || `${activityLabel(event)}-${index}`,
    label: activityLabel(event),
  }));
});
const responseData = response => response?.data?.data ?? response?.data ?? response;
const currentUserId = computed(() =>
  firstValue(source.value, ['user_id', 'user.id'])
);
const loadGamification = async userId => {
  gamificationRequest += 1;
  const request = gamificationRequest;
  gamification.value.loading = true;
  const [rulesResponse, activitiesResponse, profileResponse] = await Promise.all([
    GamificationAPI.rules().catch(() => null),
    GamificationAPI.activities({ limit: 4 }).catch(() => null),
    userId ? GamificationAPI.profile(userId).catch(() => null) : null,
  ]);

  if (request !== gamificationRequest) return;

  gamification.value = {
    activities: Array.isArray(responseData(activitiesResponse))
      ? responseData(activitiesResponse)
      : [],
    loading: false,
    profile: responseData(profileResponse),
    rules: Array.isArray(responseData(rulesResponse))
      ? responseData(rulesResponse)
      : [],
  };
};
watch(currentUserId, loadGamification, { immediate: true });
const visitPointRules = computed(() =>
  gamification.value.rules
    .filter(rule => rule.active && rule.event_key === 'visit_completed')
    .slice(0, 3)
);
const unavailablePointRuleSlots = computed(() =>
  Math.max(0, 3 - visitPointRules.value.length)
);
const activityFeed = computed(() => {
  const activityItems = gamification.value.activities;
  if (!activityItems.length) return visitActivities.value;

  return activityItems.slice(0, 4).map((activity, index) => ({
    actor: activity.actor?.name || activity.user?.name || 'Sistema',
    avatar: activity.actor?.avatar_url || activity.user?.avatar_url || '',
    date: formatDateTime(activity.occurred_at),
    id: activity.id || `${activity.title}-${index}`,
    label: activity.title || activity.body || 'Atividade de pontuação',
  }));
});
const achievements = computed(() =>
  Array.isArray(gamification.value.profile?.achievements)
    ? gamification.value.profile.achievements.slice(0, 3)
    : []
);
const saveStatusLabel = computed(() => {
  if (saving.value) return 'Salvando alterações';
  if (model.notice) return 'Alterações salvas';

  return 'Pronto para salvar';
});
const saveStatusDescription = computed(() => {
  if (saving.value) return 'Enviando alterações para a API.';
  if (model.notice) return model.notice;

  return 'As alterações serão enviadas pela ação de salvar.';
});
const visitTypeOptions = computed(
  () => model.fields.find(field => field.key === 'visit_type')?.options || []
);
const hasAction = action =>
  model.actionOptions.some(option => option.name === action);
const canConfirm = computed(() => hasAction('confirm'));
const canPrimaryAction = computed(() => model.canSubmit || canConfirm.value);
const updateDateTimePart = (key, part, value) => {
  const [currentDate = '', currentTime = ''] = String(
    model.form[key] || ''
  ).split('T');
  const date = part === 'date' ? value : currentDate;
  const time = part === 'time' ? value : currentTime;

  model.form[key] = date && time ? `${date}T${time}` : '';
};
const scheduledDateDisplay = computed(() =>
  formatDateInput(model.form.scheduled_at)
);
const scheduledTime = computed(
  () => String(model.form.scheduled_at || '').split('T')[1] || ''
);
const updateScheduledDate = event => {
  const [day, month, year] = String(event.target.value || '').split('/');
  if (!day || !month || !year) return;

  updateDateTimePart(
    'scheduled_at',
    'date',
    `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  );
};
const visitTypeLabel = value =>
  ({
    message: 'Visita por mensagem',
    presencial: 'Visita presencial',
    video: 'Visita por vídeo',
  })[value] || value;
const durationMinutes = computed({
  get: () => {
    const startsAt = new Date(model.form.scheduled_at);
    const endsAt = new Date(model.form.ends_at);
    const difference = endsAt.valueOf() - startsAt.valueOf();

    if (difference > 0) return String(Math.round(difference / 60000));

    return String(
      firstValue(source.value, [
        'additional_attributes.visit_scheduling_policy.duration_minutes',
        'visit_scheduling_policy.duration_minutes',
      ]) || ''
    );
  },
  set: value => {
    const startsAt = new Date(model.form.scheduled_at);
    if (!value || Number.isNaN(startsAt.valueOf())) {
      model.form.ends_at = '';
      return;
    }

    model.form.ends_at = toLocalDateTimeInput(
      startsAt.valueOf() + Number(value) * 60000
    );
  },
});
const durationOptions = computed(() => {
  const current = durationMinutes.value;
  const values = ['30', '45', '60', '90', '120'];

  return current && !values.includes(current) ? [current, ...values] : values;
});
const fieldValue = (key, record = source.value) => {
  const paths = {
    contact_id: ['contact_id', 'contact.id', 'client.id'],
    ends_at: ['ends_at', 'scheduled_end_at', 'end_at'],
    imovel_id: ['imovel_id', 'property_id', 'imovel.id', 'property.id'],
    scheduled_at: ['scheduled_at', 'scheduled_for', 'starts_at'],
  };

  return firstValue(record, paths[key] || [key]);
};
const displayFieldValue = (field, value) => {
  if (!value) return 'Não informado';
  if (field.type === 'datetime-local') return formatDateTime(value);
  return String(value);
};
const changeRows = computed(() =>
  model.fields.slice(0, 5).map(field => ({
    current: displayFieldValue(field, model.form[field.key]),
    label: field.label,
    previous: displayFieldValue(field, fieldValue(field.key)),
  }))
);
const summaryRows = computed(() => {
  const scheduledRow = changeRows.value.find(
    row => row.label === 'Início'
  );

  return scheduledRow ? [scheduledRow] : changeRows.value.slice(0, 1);
});
const saveAndConfirm = async () => {
  if (model.canSubmit) {
    const saved = await model.saveEdit();
    if (!saved) return;
  }

  if (canConfirm.value) await model.runAction('confirm');
};
</script>

<template>
  <main
    data-imudei-visual-id="visit-edit-root"
    class="relative ml-[42px] w-[1269px] max-w-none px-0 pb-[96px] pt-2 text-[#101958]"
  >
    <header
      data-imudei-visual-id="visit-edit-header"
      class="flex h-[92px] w-[970px] items-start justify-between gap-5"
    >
      <div>
        <p class="text-[10px] font-medium text-[#53618a]">
          Visitas <span class="mx-2 text-[#a5adc1]">/</span>{{ visitCode }}
          <span class="mx-2 text-[#a5adc1]">/</span>Editar
        </p>
        <div class="mt-0.5 flex flex-wrap items-center gap-4">
          <h1 class="text-[36px] font-semibold leading-[1.08] tracking-[-0.05em]">Editar visita</h1>
          <span
            class="flex items-center gap-1.5 rounded-full border border-[#ccefe3] bg-[#f1fcf7] px-3 py-1.5 text-[9px] font-medium text-emerald-700"
          >
            <i class="i-lucide-check size-3" />{{ saveStatusLabel }}
          </span>
        </div>
        <p class="mt-1.5 flex items-center gap-2 text-[10px] text-[#627096]">
          <i class="i-lucide-shield-check size-3.5 text-emerald-600" />
          {{ saveStatusDescription }}
        </p>
      </div>
      <div class="flex items-center gap-2 pt-[33px] text-[9px]">
        <span class="mr-2">Colaborando agora</span>
        <span
          v-for="collaborator in collaborators.slice(0, 4)"
          :key="collaborator.id"
          class="-ml-2 overflow-hidden rounded-full border-2 border-white"
        >
          <img
            v-if="collaborator.avatar"
            :src="collaborator.avatar"
            :alt="collaborator.name"
            class="size-8 object-cover"
          />
          <span
            v-else
            class="grid size-8 place-items-center bg-violet-100 text-[8px] font-bold text-[#5a24c7]"
          >{{ collaborator.initials }}</span>
        </span>
        <span
          v-if="collaborators.length > 4"
          class="grid size-8 place-items-center rounded-full border border-[#ddd7eb] bg-white text-[8px]"
        >+{{ collaborators.length - 4 }}</span>
      </div>
    </header>

    <p
      v-if="model.localError || error"
      class="absolute left-0 top-[96px] z-20 w-[970px] rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[10px] text-rose-700"
    >
      {{ model.localError || error }}
    </p>
    <p
      v-else-if="model.notice"
      class="absolute left-0 top-[96px] z-20 w-[970px] rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[10px] text-emerald-700"
    >
      {{ model.notice }}
    </p>

    <section class="mt-4 grid grid-cols-[478px_477px_288px] items-start gap-3">
      <section class="space-y-3">
        <article
          data-imudei-visual-id="visit-edit-progress"
          class="h-[318px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4"
        >
          <h2 class="text-[16px] font-semibold leading-5 tracking-[-0.025em]">Data e horário</h2>
          <div class="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
            <label class="text-[10px] font-medium">
              <span class="text-[#4a5680]">Data da visita</span>
              <span class="relative mt-1 block">
                <i class="i-lucide-calendar-days pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[#514a82]" />
                <input :value="scheduledDateDisplay" class="h-10 w-full rounded-lg border border-[#e7e2ea] bg-[#fffefe] pl-9 pr-8 text-[11px] text-[#35416b] outline-none focus:border-[#8a5ae8] disabled:border-[#e7e2ea] disabled:bg-[#fffefe] disabled:text-[#35416b] disabled:opacity-100" inputmode="numeric" placeholder="dd/mm/aaaa" type="text" :disabled="!model.canEdit || saving" @input="updateScheduledDate" />
                <i class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-[#7e86a5]" />
              </span>
            </label>
            <label class="text-[10px] font-medium">
              <span class="text-[#4a5680]">Horário</span>
              <span class="relative mt-1 block">
                <i class="i-lucide-clock-3 pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-[#514a82]" />
                <input :value="scheduledTime" class="h-10 w-full rounded-lg border border-[#e7e2ea] bg-[#fffefe] pl-9 pr-8 text-[11px] text-[#35416b] outline-none focus:border-[#8a5ae8] disabled:border-[#e7e2ea] disabled:bg-[#fffefe] disabled:text-[#35416b] disabled:opacity-100" placeholder="00:00" type="text" :disabled="!model.canEdit || saving" @input="updateDateTimePart('scheduled_at', 'time', $event.target.value)" />
                <i class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-[#7e86a5]" />
              </span>
            </label>
            <label class="text-[10px] font-medium">
              <span class="text-[#4a5680]">Tipo de visita</span>
              <span class="relative mt-1 block">
                <select v-model="model.form.visit_type" class="h-10 w-full appearance-none rounded-lg !border !border-[#e7e2ea] bg-[#fffefe] px-3 pr-9 text-[11px] text-[#35416b] outline-none focus:!border-[#8a5ae8] disabled:!border-[#e7e2ea] disabled:!bg-[#fffefe] disabled:text-[#35416b] disabled:opacity-100" :disabled="!model.canEdit || saving">
                  <option value="">Não informado</option>
                  <option v-for="option in visitTypeOptions" :key="option" :value="option">{{ visitTypeLabel(option) }}</option>
                </select>
                <i class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-[#7e86a5]" />
              </span>
            </label>
            <label class="text-[10px] font-medium">
              <span class="text-[#4a5680]">Duração estimada</span>
              <span class="relative mt-1 block">
                <select v-model="durationMinutes" class="h-10 w-full appearance-none rounded-lg !border !border-[#e7e2ea] bg-[#fffefe] px-3 pr-9 text-[11px] text-[#35416b] outline-none focus:!border-[#8a5ae8] disabled:!border-[#e7e2ea] disabled:!bg-[#fffefe] disabled:text-[#35416b] disabled:opacity-100" :disabled="!model.canEdit || saving">
                  <option value="">Não informado</option>
                  <option v-for="option in durationOptions" :key="option" :value="option">{{ formatDuration(option) }}</option>
                </select>
                <i class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-[#7e86a5]" />
              </span>
            </label>
            <label class="text-[10px] font-medium">
              <span class="text-[#4a5680]">Status da visita</span>
              <span class="mt-1 flex h-10 items-center gap-2 rounded-lg border border-[#e7e2ea] bg-[#fffefe] px-3 text-[11px] font-medium text-[#33436f]">
                <i class="i-lucide-circle-dot size-3.5 text-[#5922c8]" />
                {{ status }}
              </span>
            </label>
          </div>
        </article>

        <article
          data-imudei-visual-id="visit-edit-steps"
          class="h-[385px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4"
        >
          <h2 class="text-[16px] font-semibold leading-5">Participantes</h2>
          <section class="mt-3">
            <label class="text-[10px] text-[#57648e]">Cliente</label>
            <div v-if="clients.length" class="mt-1">
              <div
                v-for="participant in clients"
                :key="participant.id"
                class="flex h-[75px] items-center gap-3 rounded-xl border border-[#e9e4ec] bg-[#fffefe] p-3"
              >
                <img
                  v-if="participant.avatar"
                  :src="participant.avatar"
                  :alt="participant.name"
                  class="size-11 rounded-full object-cover"
                />
                <span
                  v-else
                  class="grid size-11 shrink-0 place-items-center rounded-full bg-violet-100 text-[10px] font-bold text-[#5b25c7]"
                >{{ participant.initials }}</span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-[11px]">{{ participant.name }} <em class="ml-1 rounded bg-violet-100 px-1.5 py-0.5 text-[8px] not-italic text-[#5b25c7]">Principal</em></strong>
                  <small class="block truncate text-[9px] leading-4 text-[#68749a]">{{ participant.phone || participant.email || 'Contato não informado' }}</small>
                  <small class="block truncate text-[9px] leading-4 text-[#68749a]">{{ participant.email || 'E-mail não informado' }}</small>
                </span>
                <span class="grid size-7 place-items-center rounded-full border border-[#e0dbe8] bg-white text-[#8c72bb]"><i class="i-lucide-lock-keyhole size-3" /></span>
              </div>
            </div>
            <p v-else class="mt-1 flex h-[75px] items-center gap-3 rounded-xl border border-[#e9e4ec] bg-[#fffefe] px-3 text-[10px] text-[#69759c]"><span class="grid size-11 place-items-center rounded-full bg-[#f4f1f8] text-[12px] text-[#9a92ac]">—</span><span><strong class="block text-[11px] text-[#586488]">—</strong><small class="mt-1 block text-[9px]">Cliente não retornado</small></span><span class="ml-auto grid size-7 place-items-center rounded-full border border-[#e5e0e9] text-[#a49ab5]"><i class="i-lucide-lock-keyhole size-3" /></span></p>
            <p class="mt-2 flex h-9 items-center justify-center gap-2 rounded-lg border border-dashed border-[#9e70ff] bg-[#fffefe] text-[10px] font-medium text-[#8c72bb]"><i class="i-lucide-lock-keyhole size-3.5" />Cliente não alterável</p>
          </section>
          <section class="mt-5">
            <label class="text-[10px] text-[#57648e]">Corretor responsável</label>
            <div v-if="brokers.length" class="mt-1">
              <div
                v-for="participant in brokers"
                :key="participant.id"
                class="flex h-[75px] items-center gap-3 rounded-xl border border-[#e9e4ec] bg-[#fffefe] p-3"
              >
                <img
                  v-if="participant.avatar"
                  :src="participant.avatar"
                  :alt="participant.name"
                  class="size-11 rounded-full object-cover"
                />
                <span
                  v-else
                  class="grid size-11 shrink-0 place-items-center rounded-full bg-violet-100 text-[10px] font-bold text-[#5b25c7]"
                >{{ participant.initials }}</span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-[11px]">{{ participant.name }} <em class="ml-1 rounded bg-violet-100 px-1.5 py-0.5 text-[8px] not-italic text-[#5b25c7]">Principal</em></strong>
                  <small class="block truncate text-[9px] leading-4 text-[#68749a]">{{ participant.phone || participant.email || 'Contato não informado' }}</small>
                  <small class="block truncate text-[9px] leading-4 text-[#68749a]">{{ participant.email || 'E-mail não informado' }}</small>
                </span>
                <span class="grid size-7 place-items-center rounded-full border border-[#e0dbe8] bg-white text-[#8c72bb]"><i class="i-lucide-lock-keyhole size-3" /></span>
              </div>
            </div>
            <p v-else class="mt-1 flex h-[75px] items-center gap-3 rounded-xl border border-[#e9e4ec] bg-[#fffefe] px-3 text-[10px] text-[#69759c]"><span class="grid size-11 place-items-center rounded-full bg-[#f4f1f8] text-[12px] text-[#9a92ac]">—</span><span><strong class="block text-[11px] text-[#586488]">—</strong><small class="mt-1 block text-[9px]">Corretor não retornado</small></span><span class="ml-auto grid size-7 place-items-center rounded-full border border-[#e5e0e9] text-[#a49ab5]"><i class="i-lucide-lock-keyhole size-3" /></span></p>
            <p class="mt-2 flex h-9 items-center justify-center gap-2 rounded-lg border border-dashed border-[#9e70ff] bg-[#fffefe] text-[10px] font-medium text-[#8c72bb]"><i class="i-lucide-lock-keyhole size-3.5" />Corretor não alterável</p>
          </section>
        </article>
      </section>

      <section data-imudei-visual-id="visit-edit-form" class="space-y-3">
        <article class="h-[215px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <h2 class="flex items-center gap-2 text-[14px] font-medium"><i class="i-lucide-house size-4 text-[#5b25c7]" />Imóvel</h2>
          <div class="mt-3 flex gap-4">
            <img v-if="propertyImage" :src="propertyImage" :alt="propertyTitle" class="h-[136px] w-[214px] rounded-lg object-cover" />
            <div v-else class="grid h-[136px] w-[214px] shrink-0 place-items-center rounded-lg border border-dashed border-[#e7e2ea] bg-[#fcfbfd] px-4 text-[#a29aae]">
              <span class="grid place-items-center gap-2 text-center"><i class="i-lucide-house size-7" /><small class="text-[9px]">Imagem não disponível</small></span>
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <strong :title="propertyTitle" class="block truncate text-[12px] leading-4">{{ propertyTitle }}</strong>
              <small class="mt-2 block line-clamp-2 text-[10px] leading-4 text-[#3f4d79]">{{ propertyAddress }}</small>
              <small v-if="propertyLocation" class="mt-1 block truncate text-[9px] text-[#617097]">{{ propertyLocation }}</small>
              <span v-if="propertyFacts.length" class="mt-4 flex flex-wrap gap-x-3 gap-y-2 text-[9px] text-[#59678e]"><small v-for="fact in propertyFacts" :key="fact.label" class="flex items-center gap-1"><i :class="fact.icon" class="size-3.5 text-[#64729a]" />{{ fact.label }}</small></span>
            </div>
          </div>
        </article>

        <article class="h-[135px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <h2 class="flex items-center gap-2 text-[13px] font-medium"><i class="i-lucide-lock-keyhole size-4 text-emerald-600" />Instruções de acesso</h2>
          <div v-if="hasAccessInformation" class="mt-3 flex min-h-[65px] flex-col justify-center rounded-lg border border-[#caeee2] bg-[#effbf7] px-3 py-2.5">
            <p v-if="accessInstructions" class="text-[10px] leading-5 text-[#385174]">{{ accessInstructions }}</p>
            <div v-if="accessDetails.length" class="mt-2 flex flex-wrap gap-1.5">
              <span v-for="detail in accessDetails" :key="detail.label" class="rounded-full border border-[#d7f0e7] bg-white/70 px-2 py-1 text-[7px] text-[#4b6686]"><b class="font-semibold text-[#385174]">{{ detail.label }}:</b> {{ detail.value }}</span>
            </div>
          </div>
          <div v-else class="mt-3 flex min-h-[65px] items-center rounded-lg border border-[#d9f1e8] bg-[#effbf7] px-3 text-[10px] leading-5 text-[#647a91]">
            <i class="i-lucide-key-round mr-2 size-3.5 shrink-0 text-emerald-500" />
            <span>Informações de acesso não retornadas.<br />Confirme com o responsável antes da visita.</span>
          </div>
        </article>

        <article class="h-[203px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <h2 class="flex items-center gap-2 text-[13px] font-medium"><i class="i-lucide-bell size-4" />Notificações</h2>
          <div class="mt-3 overflow-hidden rounded-xl border border-[#eee9f3]">
            <div class="flex h-[52px] items-center gap-3 border-b border-[#f0edf5] px-3"><i class="i-lucide-message-circle size-5 text-[#16a34a]" /><span class="min-w-0 flex-1"><strong class="block text-[11px]">WhatsApp</strong><small class="block text-[9px] text-[#68749a]">Preferência não retornada pela API</small></span><span class="h-5 w-9 rounded-full bg-[#e8e4ee] p-0.5"><span class="block size-4 rounded-full bg-white" /></span></div>
            <div class="flex h-[52px] items-center gap-3 px-3"><i class="i-lucide-mail size-5 text-[#5922c8]" /><span class="min-w-0 flex-1"><strong class="block text-[11px]">E-mail</strong><small class="block text-[9px] text-[#68749a]">Preferência não retornada pela API</small></span><span class="h-5 w-9 rounded-full bg-[#e8e4ee] p-0.5"><span class="block size-4 rounded-full bg-white" /></span></div>
          </div>
          <div class="mt-3 flex items-center justify-between text-[8px]"><span class="text-[#536188]">Lembrete aos participantes</span><span class="flex items-center gap-1 rounded-lg border border-[#e6e0eb] bg-white px-3 py-2 text-[#8c72bb]"><i class="i-lucide-clock-3 size-3" />Indisponível</span></div>
        </article>

        <article class="h-[128px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <header class="flex items-center justify-between gap-3"><h2 class="text-[12px] font-medium">Resumo das alterações</h2><span class="rounded-full border border-[#eee9f3] bg-[#fffefe] px-3 py-1 text-[8px] text-[#69759c]">Comparar versões</span></header>
          <div class="mt-4 grid grid-cols-[1fr_1fr_1fr] gap-3 border-b border-[#eeeaf4] pb-2 text-[8px] font-semibold text-[#6e789c]"><span>Campo alterado</span><span>Valor anterior</span><span>Novo valor</span></div>
          <div v-for="row in summaryRows" :key="row.label" class="grid grid-cols-[1fr_1fr_1fr] gap-3 py-2 text-[8px]"><span class="truncate text-[#4f5e88]">{{ row.label }}</span><span class="truncate text-rose-600">{{ row.previous }}</span><strong class="truncate text-emerald-700">{{ row.current }}</strong></div>
        </article>
      </section>

      <aside data-imudei-visual-id="visit-edit-sidebar" class="ml-[2px] -mt-[97px] h-[804px] space-y-3">
        <article class="h-[169px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <h2 class="flex items-center gap-2 text-[14px] font-semibold"><i class="i-lucide-target size-5 text-[#f43f5e]" />Missão do dia</h2>
          <div class="mt-3 flex h-[99px] items-center gap-3 rounded-xl border border-[#eee9f3] p-3">
            <span class="grid size-12 shrink-0 place-items-center rounded-full border-[4px] border-[#f1dff1] text-[12px] font-bold text-[#a787bb]">—</span>
            <span><strong class="block text-[11px] text-[#40507d]">Missão indisponível</strong><small class="mt-1 block text-[9px] leading-4 text-[#7a86a4]">A API de gamificação não expõe missão diária.</small></span>
          </div>
        </article>
        <article class="h-[203px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <h2 class="text-[12px] font-medium">Como ganhar pontos</h2>
          <div v-if="visitPointRules.length" class="mt-4 space-y-3">
            <div v-for="rule in visitPointRules" :key="rule.id" class="flex items-center gap-3">
              <i class="i-lucide-calendar-check size-4 text-[#5922c8]" />
              <span class="flex-1 text-[9px] text-[#40507d]">{{ rule.name }}</span>
              <b class="text-[8px] font-medium text-emerald-600">+{{ rule.points }} pts</b>
            </div>
            <div v-for="slot in unavailablePointRuleSlots" :key="`unavailable-rule-${slot}`" class="flex items-center gap-3 text-[#9aa2b7]">
              <i class="i-lucide-circle-dashed size-4" />
              <span class="flex-1 text-[9px]">Regra adicional não retornada</span>
              <i class="i-lucide-lock-keyhole size-3" />
            </div>
          </div>
          <div v-else class="mt-4 space-y-3 text-[#9aa2b7]">
            <div v-for="slot in 3" :key="`empty-rule-${slot}`" class="flex items-center gap-3">
              <i class="i-lucide-circle-dashed size-4" />
              <span class="flex-1 text-[9px]">{{ gamification.loading ? 'Carregando regra de pontuação…' : 'Regra não retornada pela API' }}</span>
              <i class="i-lucide-lock-keyhole size-3" />
            </div>
          </div>
          <p class="mt-3 rounded-lg bg-[#f7f6f9] px-3 py-2 text-[9px] text-[#8a94ad]">Somente regras ativas da API pontuam.</p>
        </article>
        <article class="h-[215px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <header class="flex items-center justify-between"><h2 class="text-[12px] font-medium">Feed de atividade</h2><span class="text-[9px] text-[#5922c8]">{{ activityFeed.length ? 'Ver tudo' : '—' }}</span></header>
          <div v-if="activityFeed.length" class="mt-3 space-y-3"><div v-for="activity in activityFeed" :key="activity.id" class="flex items-center gap-2"><img v-if="activity.avatar" :src="activity.avatar" :alt="activity.actor" class="size-7 shrink-0 rounded-full object-cover" /><span v-else class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[9px] font-bold text-[#5a24c7]">{{ initials(activity.actor) }}</span><span class="min-w-0 flex-1"><strong class="block truncate text-[9px]">{{ activity.actor }}</strong><small class="block truncate text-[8px] text-[#68749a]">{{ activity.label }}</small></span><small class="text-[8px] text-[#68749a]">{{ activity.date }}</small></div></div>
          <div v-else class="mt-3 flex items-center gap-2 text-[9px] text-[#9aa2b7]"><span class="grid size-7 place-items-center rounded-full bg-[#f7f5fa]">—</span><span>Atividade não retornada</span></div>
        </article>
        <article class="h-[108px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4">
          <header class="flex items-center justify-between"><h2 class="text-[12px] font-medium">Conquistas</h2><span class="text-[9px] text-[#5922c8]">{{ achievements.length ? 'Ver todas' : '—' }}</span></header>
          <div v-if="achievements.length" class="mt-3 flex items-center gap-5"><span v-for="achievement in achievements" :key="achievement.id || achievement.key" :title="achievement.title" class="grid size-10 place-items-center rounded-xl border border-[#e7e1ed] bg-[#fcfbfd] text-[#5b25c7]"><i class="i-lucide-award size-5" /></span></div>
          <p v-else class="mt-3 text-[9px] text-[#9aa2b7]">Conquistas não retornadas para o corretor.</p>
        </article>
        <article class="h-[61px] rounded-2xl border border-[#eeeaf0] bg-[#fffefe] p-4"><div class="flex gap-3"><i class="i-lucide-shield-check size-5 text-[#5922c8]" /><span><strong class="block text-[11px]">Integridade em primeiro lugar</strong><small class="block text-[9px] text-[#68749a]">Regras e ações conforme a API.</small></span></div></article>
      </aside>
    </section>

    <footer
      data-imudei-visual-id="visit-edit-footer"
      class="fixed bottom-[13px] left-[253px] right-0 z-50 h-[71px] border-t border-[#eeeaf0] bg-[#fffefd]/95 shadow-[0_-8px_25px_rgba(38,25,86,0.04)] backdrop-blur"
    >
      <span class="absolute left-4 top-1/2 i-lucide-badge-check size-5 -translate-y-1/2 text-[#66739b]" />
      <div class="absolute left-[266px] top-1/2 flex -translate-y-1/2 gap-[19px]">
        <button class="h-10 min-w-[185px] rounded-lg !border !border-[#8e65ef] bg-white text-[10px] font-semibold text-[#5421c7] disabled:!border-[#e6dff6] disabled:!bg-white disabled:text-[#b6a3e8] disabled:opacity-100" type="button" :disabled="saving" @click="emit('navigate', 'detail')">Cancelar</button>
        <button class="ml-3 flex h-10 min-w-[180px] items-center justify-center gap-2 rounded-lg !border !border-[#8e65ef] bg-white text-[10px] font-semibold text-[#5421c7] disabled:!border-[#e6dff6] disabled:!bg-white disabled:text-[#b6a3e8] disabled:cursor-not-allowed disabled:opacity-100" type="button" disabled><i class="i-lucide-save size-4" />Rascunho indisponível</button>
        <button class="flex h-10 min-w-[294px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#cf20d6] via-[#8c21e4] to-[#2768ec] text-[10px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="saving || !canPrimaryAction" @click="saveAndConfirm"><i class="i-lucide-circle-check size-4" />{{ saving ? 'Salvando…' : canConfirm ? 'Salvar alterações e confirmar visita' : 'Salvar alterações' }}</button>
      </div>
      <span class="absolute right-4 top-1/2 -translate-y-1/2 text-right text-[8px] text-[#8e95aa]">{{ canConfirm ? 'Confirmação disponível na API' : '—' }}</span>
    </footer>
  </main>
</template>
